import React from "react";
import axios from "axios";

import { Action, Category } from "@/constants/events";
import { FeaturedDispensary } from "@/custom-types/FeaturedDispensary";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useSelector } from "@/redux/hooks";
import { getFormattedLocation } from "@/redux/selectors/location";
import { getUserId } from "@/redux/selectors/user";
import { getRoundedDistance } from "@/utils/distance";
import { localizeText } from "@/utils/localizeText";

import Carousel from "@/components/botanic/Carousel";
import PickupReadinessDispensaryCard from "@/components/PickupReadinessDispensaryCard";
import TrackImpression from "@/components/TrackImpression";

const DISPENSARY_CARD_VARIANT = "platinum";

export type FeaturedDispensariesProps = {
  className?: string;
  featuredDispensaries: FeaturedDispensary[];
  isDelta9Allowed: boolean | null;
};

const FeaturedDispensariesSection = ({
  className = "",
  featuredDispensaries,
  isDelta9Allowed,
}: FeaturedDispensariesProps) => {
  const { publishEvent } = useEventTracker();
  const countryCode = useDomainCountryCode();
  const userId = useSelector(getUserId);
  const formattedLocation = useSelector(getFormattedLocation);

  const carouselTitle = `${isDelta9Allowed ? "Sponsored dispensaries" : "Shops"} near you in ${formattedLocation}`;

  const localizedRetailType = isDelta9Allowed ? "dispensaries" : "stores";

  return featuredDispensaries?.length ? (
    <>
      <TrackImpression
        category="Featured Dispensaries Carousel"
        label="Featured Dispensaries Carousel"
        data-testid="featured-dispensaries-section"
        className={className}
      >
        <div className="container">
          <Carousel
            title={<h2 className="heading--m">{carouselTitle}</h2>}
            headingClassName="heading--m"
            seeMoreText={`see all ${localizeText(localizedRetailType, countryCode)}`}
            seeMoreHref="/dispensaries"
            showSeeMoreCard={true}
            seeMoreOnClick={() => {
              publishEvent({
                action: Action.click,
                category: Category.Carousel,
                label: "home featured dispensaries carousel show all",
              });
            }}
            seeMoreCardClass="rounded overflow-hidden shadow-low"
          >
            {featuredDispensaries.map((dispensary, slot) => {
              const deliveryEnabled =
                !!dispensary.deliveryEnabled ||
                Boolean(dispensary.currentStatuses?.delivery);
              const pickupEnabled =
                !!dispensary.pickupEnabled ||
                Boolean(dispensary.currentStatuses?.pickup);

              const customDimensions = {
                deal_id: dispensary.featuredDeal?.id,
                dispensaryDistance: getRoundedDistance(
                  dispensary.distanceMi,
                  countryCode,
                ),
                dispensaryId: dispensary.id,
                merchandisingCampaignId: dispensary.merchandisingCampaignId,
                userId: userId as number,
              };

              const impressionLabel = `dispensary card`;

              const eventOnClick = () => {
                publishEvent({
                  action: Action.click,
                  category: Category.homepageFeature,
                  label: impressionLabel,
                  ...customDimensions,
                });
              };

              const trackCtaClick = () => {
                publishEvent({
                  action: Action.click,
                  category: Category.homepageFeature,
                  label: `${impressionLabel} button`,
                  ...customDimensions,
                });
              };

              return (
                <TrackImpression
                  category={Category.homepageFeature}
                  className="h-full w-full relative"
                  key={`featured-dispensary-${slot}`}
                  label={impressionLabel}
                  trackingFunction={() => {
                    publishEvent({
                      action: Action.impression,
                      category: Category.homepageFeature,
                      // @ts-ignore -- TODO: Fix publishEvent types (https://leafly.atlassian.net/browse/SHH-1133)
                      deal_id: dispensary.featuredDeal?.id,
                      dispensaryDistance: getRoundedDistance(
                        dispensary.distanceMi,
                        countryCode,
                      ),
                      dispensaryId: dispensary.id,
                      label: impressionLabel,
                      merchandisingCampaignId:
                        dispensary.merchandisingCampaignId,
                      userId: userId as number,
                    });
                    // this is for kevel impression tracking, ask kayleigh
                    if (dispensary.impressionUrl) {
                      axios.get(dispensary.impressionUrl);
                    }
                  }}
                >
                  <PickupReadinessDispensaryCard
                    coverPhotoSizes={[196, null, null, 207, null, 207]}
                    ctaOnClick={trackCtaClick}
                    currentStatus={dispensary.currentStatuses?.store}
                    data-testid={`featured-dispensaries-section-card-${dispensary.id}`}
                    deal={dispensary.featuredDeal}
                    deliveryEnabled={deliveryEnabled}
                    disableLazyLoad={slot < 4}
                    dispensaryId={dispensary.id}
                    distanceMi={dispensary.distanceMi}
                    flags={dispensary.flags}
                    href={dispensary.href}
                    inStoreCartEnabled={dispensary.inStoreCartEnabled}
                    logoUrl={dispensary.logoUrl}
                    name={dispensary.name}
                    onClick={eventOnClick}
                    photoUrl={dispensary.coverPhotoUrl}
                    pickupEnabled={pickupEnabled}
                    preorderConfigured={dispensary.preorderConfigured}
                    preserveHeight={true}
                    rating={dispensary.starRating || dispensary.rating}
                    reviewCount={dispensary.reviewCount}
                    scheduleType="store"
                    showDeal={true}
                    slug={dispensary.slug}
                    tags={dispensary.tags}
                    timeZone={dispensary.timeZone}
                    variant={DISPENSARY_CARD_VARIANT}
                    preload={slot < 4}
                  />
                </TrackImpression>
              );
            })}
          </Carousel>
        </div>
      </TrackImpression>
    </>
  ) : null;
};

export default FeaturedDispensariesSection;
